// components/FormatBar.js
import React, { useState, useRef } from 'react';
import { Bold, Italic, Code, List, Sparkles, Tag, GitBranch, Palette } from 'lucide-react';
import styles from './FormatBar.module.css';
import { useWorkspaces } from './contexts/WorkspacesContext';
import { useEditor } from './contexts/EditorContext'; 
import useKeyPointsExtraction from '../hooks/useKeyPointsExtraction';
import ExtractionModal from './ExtractionModal';

const FormatBar = () => {
  const { editor } = useEditor();
  const { updateDocumentContent, createDocument, getSelectedDocument } = useWorkspaces();
  const { extractKeyPointsOnly, createStickiesFromResults, isProcessing } = useKeyPointsExtraction();
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const lastSelection = useRef(null);

  const ToolbarButton = ({ onClick, children, active, disabled }) => (
    <button
      type="button"
      onMouseDown={(e) => { 
        e.preventDefault(); 
        onClick?.(e); 
      }}
      className={`${styles.formatButton} ${active ? styles.active : ''}`}
      disabled={disabled}
    >
      {children}
    </button>
  );

  // Function to create a new document in the current workspace
  const handleCreateDocument = async () => {
    const currentDocument = getSelectedDocument();
    
    if (!currentDocument) {
      console.log('FormatBar - No current document found');
      return;
    }
    
    // Get workspace ID from current document
    const workspaceId = currentDocument.workspace_public_id;
    
    if (!workspaceId) {
      console.error('FormatBar - Document missing workspace_public_id');
      alert('Unable to create document: workspace information missing');
      return;
    }
    
    try {
      console.log('FormatBar - Creating document in workspace:', workspaceId);
      const result = await createDocument(workspaceId);
      console.log('FormatBar - Document created:', result);
    } catch (error) {
      console.error('Error creating document:', error);
      alert('Failed to create document. Please try again.');
    }
  };

  const handleFormat = (format, value) => {
    if (!editor) return;
    
    // Try current selection first, fallback to last known selection
    let range = editor.getSelection(true);
    if (!range || range.length === 0) {
      range = lastSelection;
      if (range) {
        console.log('Using saved selection:', range);
        editor.setSelection(range.index, range.length);
        range = editor.getSelection();
      }
    }
    
    if (!range) return;
    
    if (format === 'code-block') {
      const currentFormat = editor.getFormat(range);
      editor.format('code-block', !currentFormat['code-block']);
    } else if (format === 'font') {
      // Handle font formatting
      if (range.length > 0) {
        editor.formatText(range.index, range.length, 'font', value);
      } else {
        editor.format('font', value);
      }
    } else if (format === 'size') {
      // Handle size formatting
      if (range.length > 0) {
        editor.formatText(range.index, range.length, format, value);
      } else {
        editor.format(format, value);
      }
    } else if (format === 'color') {
      // Handle color formatting
      if (range.length > 0) {
        editor.formatText(range.index, range.length, 'color', value);
      } else {
        editor.format('color', value);
      }
    } else if (range.length > 0) {
      // Text is selected - format the selection
      const current = editor.getFormat(range)[format];
      editor.formatText(range.index, range.length, format, !current);
    } else {
      // Cursor position - set typing format
      const current = editor.getFormat()[format];
      editor.format(format, !current);
    }

    // Force editor to update visually
    editor.focus();
    
    // Save content after formatting
    setTimeout(() => {
      const currentDocument = getSelectedDocument();
      if (editor && currentDocument) {
        const content = editor.root.innerHTML;
        updateDocumentContent(currentDocument.public_id, content);
      }
    }, 10);
  };
  
  // Function to handle key points extraction
  const handleExtractKeyPoints = async (options = {}) => {
    const currentDocument = getSelectedDocument();
    
    console.log('DEBUG - Editor available:', !!editor);
    console.log('DEBUG - Document available:', !!currentDocument);
    
    if (!editor || !currentDocument) {
      return { success: false, message: 'Editor or document not available' };
    }
    
    // Get workspace ID for note operations
    const workspaceId = currentDocument.workspace_public_id;
    
    if (!workspaceId) {
      return { success: false, message: 'Workspace information missing' };
    }
    
    // Call the extraction with workspace ID (notes are workspace-scoped)
    const result = await extractKeyPointsOnly(options, editor, workspaceId); 
    return result;
  };

  // Function to handle creating notes from results
  const handleCreateStickies = async (aiData, extractionTypes) => {
    const currentDocument = getSelectedDocument();
    
    if (!currentDocument) {
      return { success: false, message: 'No document available for creating notes' };
    }
    
    // Get workspace ID for note operations
    const workspaceId = currentDocument.workspace_public_id;
    
    if (!workspaceId) {
      return { success: false, message: 'Workspace information missing' };
    }
    
    // Call createStickiesFromResults with workspace ID (notes are workspace-scoped)
    const result = await createStickiesFromResults(aiData, extractionTypes, workspaceId);
    return result;
  };

  return (
    <div className={styles.formatBar}>

      <div className={styles.formatBarSection}>
        <ToolbarButton onClick={() => handleFormat('bold')}>
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => handleFormat('italic')}>
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => handleFormat('code-block')}>
          <Code size={16} />
        </ToolbarButton>

        <select 
          onChange={(e) => handleFormat('color', e.target.value)}
          className={styles.colorSelect}
          defaultValue=""
        >
          <option value="" disabled>Color</option>
          <option value="#000000">Black</option>
          <option value="#ff0000">Red</option>
          <option value="#00ff00">Green</option>
          <option value="#0000ff">Blue</option>
          <option value="#ffff00">Yellow</option>
          <option value="#ff00ff">Magenta</option>
          <option value="#00ffff">Cyan</option>
          <option value="#808080">Gray</option>
          <option value="#800000">Dark Red</option>
          <option value="#008000">Dark Green</option>
          <option value="#000080">Dark Blue</option>
          <option value="#800080">Purple</option>
        </select>
        
        <select 
          onChange={(e) => handleFormat('size', e.target.value)}
          className={styles.sizeSelect}
          defaultValue=""
        >
          <option value="" disabled>Size</option>
          <option value="small">Small</option>
          <option value={false}>Normal</option>
          <option value="large">Large</option>
          <option value="huge">Huge</option>
        </select>
        
        <select 
          onChange={(e) => handleFormat('font', e.target.value)}
          className={styles.fontSelect}
          defaultValue=""
        >
          <option value="" disabled>Font</option>
          <option value="sans-serif">Sans Serif</option>
          <option value="serif">Serif</option>
          <option value="monospace">Monospace</option>
          <option value="fira-sans">Fira Sans</option>
          <option value="playfair">Playfair Display</option>
          <option value="roboto">Roboto</option>
          <option value="oswald">Oswald</option>
          <option value="inconsolata">Inconsolata</option>
        </select>
      </div>

      <div className={styles.formatBarDivider} />

      <div className={styles.formatBarSection}>
        <ToolbarButton onClick={() => handleFormat('list', 'bullet')}>
          <List size={16} />
        </ToolbarButton>
      </div>
            
      {/* Add a spacer div to push the next section to the right */}
      <div style={{ flex: 1 }}></div>

      <div className={styles.formatBarDivider} />
      
      {/* Key Points extraction button on the far right */}
   
    </div>
  );
};

export default FormatBar;